import { Badge, Card, Group, Stack, Text, Title } from '@mantine/core';

import { ReportRequestDetail } from '@/types/report-request';
import classes from './ClientInputCard.module.css';

// Confidence → Mantine colour for the classification badge.
const CONFIDENCE_COLORS: Record<string, string> = {
  high: 'green',
  medium: 'yellow',
  low: 'red',
};

// Freshdesk `description` is HTML; `description_text` is the plain-text twin.
// When only the HTML form is present we strip tags for a safe text render
// (rendered as text, never via dangerouslySetInnerHTML).
function htmlToPlainText(html: string): string {
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Surfaces the original client input a request was triaged from — the raw
 * inbound message (subject + body), the subjects under triage, and the AI
 * classification — so the analyst can see what they're triaging against at
 * every stage. Tolerates any subset (`rawPayload` is source-shaped and may be
 * null for non-Freshdesk requests); renders nothing when there's no input.
 */
export function ClientInputCard({ detail }: Readonly<{ detail: ReportRequestDetail }>) {
  const payload = detail.rawPayload;
  const subject = payload?.subject?.trim() || undefined;
  const bodyText =
    payload?.description_text?.trim() ||
    (payload?.description ? htmlToPlainText(payload.description) : undefined) ||
    undefined;
  const subjects = (detail.subjectStrings ?? []).map((s) => s.trim()).filter(Boolean);
  const classification = detail.classificationResult;
  const hasClassification =
    !!classification &&
    (classification.confidence !== undefined ||
      classification.is_report_request !== undefined ||
      !!classification.reasoning);

  const hasContent =
    Boolean(subject) || Boolean(bodyText) || subjects.length > 0 || hasClassification;
  if (!hasContent) {
    return null;
  }

  return (
    <Card shadow="sm" padding="md" radius="md">
      <Stack gap="sm">
        <Title order={5}>Client Input</Title>

        {(subject || bodyText) && (
          <Stack gap={4}>
            <Text size="xs" c="dimmed">
              Original request
            </Text>
            {subject && (
              <Text size="sm" fw={600}>
                {subject}
              </Text>
            )}
            {bodyText && (
              <Text size="sm" className={classes.body}>
                {bodyText}
              </Text>
            )}
          </Stack>
        )}

        {subjects.length > 0 && (
          <Stack gap={4}>
            <Text size="xs" c="dimmed">
              Subjects under triage
            </Text>
            <Group gap="xs">
              {subjects.map((s, i) => (
                <Badge key={`${s}-${i}`} variant="light" color="blue" size="sm">
                  {s}
                </Badge>
              ))}
            </Group>
          </Stack>
        )}

        {hasClassification && classification && (
          <Stack gap={4}>
            <Group gap="xs">
              <Text size="xs" c="dimmed">
                Classification
              </Text>
              {classification.confidence && (
                <Badge
                  size="sm"
                  variant="light"
                  color={CONFIDENCE_COLORS[classification.confidence] ?? 'gray'}
                >
                  {classification.confidence} confidence
                </Badge>
              )}
              {classification.is_report_request !== undefined && (
                <Badge size="sm" variant="outline" color="gray">
                  {classification.is_report_request ? 'Report request' : 'Not a report request'}
                </Badge>
              )}
            </Group>
            {classification.reasoning && (
              <Text size="sm" c="dimmed">
                {classification.reasoning}
              </Text>
            )}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}
